/**
 * Add Manual Booking Modal Component
 * Manual Bookings Implementation
 *
 * Allows vendors to add walk-in/phone bookings that didn't come through Spotts
 * - No payment processing through Spotts
 * - 100% vendor revenue (no platform commission)
 * - Completely separate from platform payouts
 * - Time slot validation to prevent double bookings
 */

import { CURRENCY_SYMBOL } from '../../utils/currency';
import React, { useState, useEffect, useRef } from 'react';
import { X, Clock, Calendar, MapPin, User, Phone, Mail, FileText, AlertCircle, DollarSign, Users, Repeat } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../ui/Modal';
import { vendorApi, OperatingHour } from '../../services/api';
import { format } from 'date-fns';

interface AddManualBookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (bookingData: ManualBookingData) => Promise<void>;
  courts: any[];
  venueId: number;
  operatingHours?: OperatingHour[];
}

export interface ManualBookingData {
  court_id: number;
  booking_date: string;
  start_time: string;
  end_time: string;
  customer_name: string;
  customer_phone?: string; // Optional - not all walk-ins provide phone
  customer_email?: string; // Optional - for sending booking confirmation
  price: number;
  payment_method: 'cash' | 'transfer' | 'pos';
  booking_source?: 'walk_in' | 'phone' | 'whatsapp'; // How the booking was made
  number_of_players?: number;
  notes?: string;
}

export interface RecurringSession {
  date: string;
  start_time: string;
  end_time: string;
  court_id: number;
  price: number;
}

export interface RecurringManualBookingData {
  sessions: RecurringSession[];
  customer_name: string;
  customer_phone?: string;
  customer_email?: string; // Optional - for sending booking confirmation
  payment_method: 'cash' | 'transfer' | 'pos';
  booking_source?: 'walk_in' | 'phone' | 'whatsapp'; // How the booking was made
  number_of_players?: number;
  notes?: string;
}

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash', icon: '💵', description: 'Payment received in cash' },
  { value: 'transfer', label: 'Bank Transfer', icon: '🏦', description: 'Payment via bank transfer' },
  { value: 'pos', label: 'POS/Card', icon: '💳', description: 'Payment via POS machine' },
];

export default function AddManualBookingModal({
  isOpen,
  onClose,
  onSubmit,
  courts,
  venueId,
  operatingHours,
}: AddManualBookingModalProps) {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Tab state
  const [activeTab, setActiveTab] = useState<'single' | 'recurring'>('single');

  // Form state (shared between single and recurring)
  const [courtId, setCourtId] = useState<number>(0);
  const [date, setDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [customerName, setCustomerName] = useState<string>('');
  const [customerPhone, setCustomerPhone] = useState<string>('');
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [price, setPrice] = useState<string>('');
  const [paymentMethod, setPaymentMethod] = useState<ManualBookingData['payment_method']>('cash');
  const [bookingSource, setBookingSource] = useState<'walk_in' | 'phone' | 'whatsapp'>('walk_in');
  const [numberOfPlayers, setNumberOfPlayers] = useState<string>('');
  const [notes, setNotes] = useState<string>('');

  // Recurring session state
  const [sessions, setSessions] = useState<RecurringSession[]>([]);
  const [customDate, setCustomDate] = useState<string>('');

  // Smart fill state
  const [smartFillDays, setSmartFillDays] = useState<number[]>([]);
  const [smartFillDuration, setSmartFillDuration] = useState<'1m' | '3m' | '6m' | '12m' | 'custom'>('3m');
  const [smartFillStartDate, setSmartFillStartDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [smartFillEndDate, setSmartFillEndDate] = useState<string>('');

  // "Same for all" toggle states (reserved for future UI enhancements)
  const [, setSameTimeForAll] = useState<boolean>(true);
  const [, setSameCourtForAll] = useState<boolean>(true);
  const [, setSamePriceForAll] = useState<boolean>(true);

  // Customer autocomplete state
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState<Array<{ name: string; phone: string | null; email: string | null; source: 'spotts' | 'manual' }>>([]);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const customerInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch existing customers with proper caching
  // Customer list is relatively stable, so we cache for 30 minutes
  // This prevents refetching every time the modal opens/closes
  const { data: customers = [] } = useQuery({
    queryKey: ['manual-booking-customers', venueId],
    queryFn: () => vendorApi.getManualBookingCustomers(venueId),
    enabled: !!venueId, // Always enabled when venueId exists (not dependent on modal open)
    staleTime: 30 * 60 * 1000, // 30 minutes - customer list doesn't change often
    gcTime: 60 * 60 * 1000, // Keep in cache for 1 hour
    refetchOnWindowFocus: false, // Don't refetch when switching tabs
    refetchOnMount: false, // Use cached data when modal opens
  });


  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab('single');
      setCourtId(0);
      setDate(new Date().toISOString().split('T')[0]);
      setStartTime('');
      setEndTime('');
      setCustomerName('');
      setCustomerPhone('');
      setCustomerEmail('');
      setPrice('');
      setPaymentMethod('cash');
      setBookingSource('walk_in');
      setNumberOfPlayers('');
      setNotes('');
      setSessions([]);
      setCustomDate('');
      setSmartFillDays([]);
      setSmartFillDuration('3m');
      setSmartFillStartDate(new Date().toISOString().split('T')[0]);
      setSmartFillEndDate('');
      setSameTimeForAll(true);
      setSameCourtForAll(true);
      setSamePriceForAll(true);
      setErrors({});

    }
  }, [isOpen, courts]);

  // Pricing mode state
  const [pricingMode, setPricingMode] = useState<'hourly' | 'per_person'>('hourly');
  const [hourlyRate, setHourlyRate] = useState<number>(0);
  const [perPersonRate, setPerPersonRate] = useState<number>(0);
  const [durationHours, setDurationHours] = useState<number>(0);
  const [courtPricingMode, setCourtPricingMode] = useState<string>('hourly');
  const [minPlayers, setMinPlayers] = useState<number>(1);
  const [maxPlayers, setMaxPlayers] = useState<number>(30);
  const [pricingSlabs, setPricingSlabs] = useState<Array<{ duration_hours: number; price: number }>>([]);

  // Detect court pricing capabilities when court changes
  useEffect(() => {
    if (courtId && courts.length > 0) {
      const selectedCourt = courts.find(c => c.id === courtId);

      if (selectedCourt) {
        // Hourly rate hierarchy
        let hRate = selectedCourt.custom_hourly_rate ||
                    selectedCourt.effective_hourly_rate ||
                    selectedCourt.sport?.hourly_rate;
        if (typeof hRate === 'string') hRate = parseFloat(hRate);
        setHourlyRate(hRate && hRate > 0 ? hRate : 0);

        // Per-person rate hierarchy
        let ppRate = selectedCourt.custom_per_person_rate ||
                     selectedCourt.effective_per_person_rate;
        if (typeof ppRate === 'string') ppRate = parseFloat(ppRate);
        setPerPersonRate(ppRate && ppRate > 0 ? ppRate : 0);

        // Pricing mode from court
        const mode = selectedCourt.effective_pricing_mode || selectedCourt.pricing_mode || 'hourly';
        setCourtPricingMode(mode);

        // Player limits
        const minP = selectedCourt.effective_min_players || selectedCourt.min_players || 1;
        const maxP = selectedCourt.effective_max_players || selectedCourt.max_players || 30;
        setMinPlayers(minP);
        setMaxPlayers(maxP);

        // Slab pricing
        const slabs: Array<{ duration_hours: number; price: number }> = selectedCourt.pricing_slabs ?? [];
        setPricingSlabs(slabs);

        // If court only supports per_person, switch to it
        if (mode === 'per_person') {
          setPricingMode('per_person');
          if (!numberOfPlayers) setNumberOfPlayers(minP.toString());
        } else {
          setPricingMode('hourly');
        }
      }
    }
  }, [courtId, courts]);

  // Auto-fill price based on pricing mode, court, duration, and players
  useEffect(() => {
    if (!courtId) return;

    // Calculate duration
    let hours = 0;
    if (startTime && endTime) {
      const [sh, sm] = startTime.split(':').map(Number);
      const [eh, em] = endTime.split(':').map(Number);
      const startMins = sh * 60 + sm;
      const endMins = eh * 60 + em;
      if (endMins > startMins) {
        hours = (endMins - startMins) / 60;
      }
    }
    setDurationHours(hours);

    // Slab courts: price is set directly in the duration dropdown onChange — don't override it here
    if (pricingSlabs.length > 0) return;

    if (pricingMode === 'per_person' && perPersonRate > 0) {
      const players = parseInt(numberOfPlayers) || minPlayers;
      const totalPrice = Math.round(perPersonRate * players);
      setPrice(totalPrice.toString());
    } else if (hourlyRate > 0) {
      const totalPrice = hours > 0 ? Math.round(hourlyRate * hours) : hourlyRate;
      setPrice(totalPrice.toString());
    }
  }, [courtId, startTime, endTime, pricingMode, numberOfPlayers, hourlyRate, perPersonRate, minPlayers, pricingSlabs]);

  // Debounced customer search (300ms delay for performance with 200+ customers)
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      if (customers.length > 0) {
        if (searchQuery.length > 0) {
          // Filter customers by name, phone, or email
          const filtered = customers
            .filter(customer =>
              customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
              (customer.phone && customer.phone.includes(searchQuery)) ||
              (customer.email && customer.email.toLowerCase().includes(searchQuery.toLowerCase()))
            )
            .slice(0, 50); // Limit to first 50 results for performance
          setFilteredCustomers(filtered);
        } else {
          // Show first 50 customers when field is empty
          setFilteredCustomers(customers.slice(0, 50));
        }
      } else {
        setFilteredCustomers([]);
      }
    }, 300); // 300ms debounce delay

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, customers]);

  // Handle customer selection from autocomplete
  const handleSelectCustomer = (customer: { name: string; phone: string | null; email: string | null; source: 'spotts' | 'manual' }) => {
    setCustomerName(customer.name);
    setCustomerPhone(customer.phone || '');
    setCustomerEmail(customer.email || '');
    setSearchQuery(''); // Clear search query
    setShowCustomerDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerInputRef.current && !customerInputRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Generate time options based on operating hours for the selected date
  const generateTimeOptions = (selectedDate?: string) => {
    const options: string[] = [];

    // Default hours if no operating hours provided
    let startHour = 6;
    let endHour = 23;

    // Get operating hours for the selected date
    if (selectedDate && operatingHours && operatingHours.length > 0) {
      try {
        const dateObj = new Date(selectedDate + 'T00:00:00');
        const dayOfWeek = format(dateObj, 'EEEE').toLowerCase();

        const daySchedule = operatingHours.find(
          (oh) => oh.day_of_week.toLowerCase() === dayOfWeek
        );

        if (daySchedule && !daySchedule.is_closed && daySchedule.opening_time && daySchedule.closing_time) {
          const openingParts = daySchedule.opening_time.split(':');
          const closingParts = daySchedule.closing_time.split(':');

          startHour = parseInt(openingParts[0], 10);
          endHour = parseInt(closingParts[0], 10);

          // If closing time has minutes > 0, extend end hour
          const closingMinutes = parseInt(closingParts[1], 10) || 0;
          if (closingMinutes > 0) {
            endHour = endHour + 1;
          }

          // Handle overnight hours (e.g., 15:00-00:00 or 18:00-02:00)
          if (endHour <= startHour) {
            endHour = endHour + 24;
          }
        }
      } catch (e) {
        console.error('Error parsing operating hours:', e);
      }
    }

    // Generate time slots
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        if (hour === endHour && minute > 0) break; // Don't go past closing time
        const displayHour = hour % 24; // Wrap around for overnight hours
        const timeStr = `${String(displayHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        options.push(timeStr);
      }
    }

    return options;
  };

  // Get time options for the currently selected date
  const timeOptions = generateTimeOptions(date);

  // End time options: filtered to start 30 min after selected start time
  // Uses index-based comparison to handle midnight (00:00) wrap-around
  const endTimeOptions = startTime
    ? (() => {
        const startIdx = timeOptions.indexOf(startTime);
        return startIdx >= 0 ? timeOptions.slice(startIdx + 1) : timeOptions;
      })()
    : timeOptions;

  // Add date to recurring sessions
  const handleAddDate = () => {
    if (!customDate) return;

    if (sessions.some(s => s.date === customDate)) {
      setErrors({ ...errors, dates: 'This date is already added' });
      return;
    }

    // Create new session with default values from form
    const newSession: RecurringSession = {
      date: customDate,
      start_time: startTime || '',
      end_time: endTime || '',
      court_id: courtId || 0,
      price: parseFloat(price) || 0,
    };

    setSessions([...sessions, newSession].sort((a, b) => a.date.localeCompare(b.date)));
    setCustomDate('');
    setErrors({ ...errors, dates: '' });
  };

  // Smart fill — generate all matching weekday dates for the chosen duration
  const handleSmartFill = () => {
    if (smartFillDays.length === 0) {
      setErrors({ ...errors, smartFill: 'Select at least one day of the week' });
      return;
    }
    if (!startTime || !endTime || !courtId || !price) {
      setErrors({ ...errors, smartFill: 'Fill in court, time, and price fields first' });
      return;
    }

    const startDate = smartFillStartDate
      ? new Date(smartFillStartDate + 'T00:00:00')
      : new Date();
    startDate.setHours(0, 0, 0, 0);

    let endDate: Date;
    if (smartFillDuration === '1m') {
      endDate = new Date(startDate); endDate.setMonth(endDate.getMonth() + 1);
    } else if (smartFillDuration === '3m') {
      endDate = new Date(startDate); endDate.setMonth(endDate.getMonth() + 3);
    } else if (smartFillDuration === '6m') {
      endDate = new Date(startDate); endDate.setMonth(endDate.getMonth() + 6);
    } else if (smartFillDuration === '12m') {
      endDate = new Date(startDate); endDate.setMonth(endDate.getMonth() + 12);
    } else {
      if (!smartFillEndDate) {
        setErrors({ ...errors, smartFill: 'Please select an end date' });
        return;
      }
      endDate = new Date(smartFillEndDate + 'T00:00:00');
    }

    const existingDates = new Set(sessions.map(s => s.date));
    const newSessions: RecurringSession[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      if (smartFillDays.includes(current.getDay())) {
        const dateStr = `${current.getFullYear()}-${String(current.getMonth() + 1).padStart(2, '0')}-${String(current.getDate()).padStart(2, '0')}`;
        if (!existingDates.has(dateStr)) {
          newSessions.push({
            date: dateStr,
            start_time: startTime,
            end_time: endTime,
            court_id: courtId,
            price: parseFloat(price) || 0,
          });
        }
      }
      current.setDate(current.getDate() + 1);
    }

    if (newSessions.length === 0) {
      setErrors({ ...errors, smartFill: 'No new dates found — all may already be added' });
      return;
    }

    if (sessions.length + newSessions.length > 100) {
      setErrors({ ...errors, smartFill: `Would create ${sessions.length + newSessions.length} sessions total — limit is 100. Reduce the duration or select fewer days.` });
      return;
    }

    setSessions([...sessions, ...newSessions].sort((a, b) => a.date.localeCompare(b.date)));
    setErrors({ ...errors, smartFill: '' });
  };

  // Remove session
  const handleRemoveSession = (index: number) => {
    setSessions(sessions.filter((_, i) => i !== index));
  };

  // Update individual session field (reserved for future per-session editing UI)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleUpdateSession = (index: number, field: keyof RecurringSession, value: any) => {
    const updated = [...sessions];
    updated[index] = { ...updated[index], [field]: value };
    setSessions(updated);
  };

  // Apply "Same for all" changes (reserved for future toggle functionality)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const applySameForAll = (field: 'time' | 'court' | 'price') => {
    if (sessions.length === 0) return;

    const updated = sessions.map(session => {
      if (field === 'time') {
        return { ...session, start_time: startTime, end_time: endTime };
      } else if (field === 'court') {
        return { ...session, court_id: courtId };
      } else if (field === 'price') {
        return { ...session, price: parseFloat(price) || 0 };
      }
      return session;
    });

    setSessions(updated);
  };

  // Validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!courtId || courtId === 0) {
      newErrors.court_id = 'Please select a court';
    }

    // Date validation differs between single and recurring
    if (activeTab === 'single') {
      if (!date) {
        newErrors.booking_date = 'Please select a date';
      }
    } else {
      // Recurring tab
      if (sessions.length === 0) {
        newErrors.sessions = 'Please add at least one session';
      } else {
        // Validate each session
        sessions.forEach((session, index) => {
          if (!session.court_id || session.court_id === 0) {
            newErrors[`session_${index}_court`] = `Session ${index + 1}: Please select a court`;
          }
          if (!session.start_time) {
            newErrors[`session_${index}_start`] = `Session ${index + 1}: Please select start time`;
          }
          if (!session.end_time) {
            newErrors[`session_${index}_end`] = `Session ${index + 1}: Please select end time`;
          }
          // Use index-based comparison for overnight hours (00:00 wraps around)
          if (session.start_time && session.end_time) {
            const sIdx = timeOptions.indexOf(session.start_time);
            const eIdx = timeOptions.indexOf(session.end_time);
            if (sIdx >= 0 && eIdx >= 0 && sIdx >= eIdx) {
              newErrors[`session_${index}_time`] = `Session ${index + 1}: End time must be after start time`;
            }
          }
          if (!session.price || session.price < 0) {
            newErrors[`session_${index}_price`] = `Session ${index + 1}: Valid price is required`;
          }
        });
      }
    }

    if (!startTime) {
      newErrors.start_time = 'Please select start time';
    }

    if (!endTime) {
      newErrors.end_time = 'Please select end time';
    }

    // Use index-based comparison for overnight hours (00:00 wraps around)
    if (startTime && endTime) {
      const sIdx = timeOptions.indexOf(startTime);
      const eIdx = timeOptions.indexOf(endTime);
      if (sIdx >= 0 && eIdx >= 0 && sIdx >= eIdx) {
        newErrors.end_time = 'End time must be after start time';
      }
    }

    if (!customerName.trim()) {
      newErrors.customer_name = 'Customer name is required';
    }

    // Customer phone is optional but validate format if provided
    if (customerPhone.trim()) {
      const phoneRegex = /^(\+234|0)[789][01]\d{8}$/;
      const cleanPhone = customerPhone.trim().replace(/[\s-]/g, '');
      if (!phoneRegex.test(cleanPhone)) {
        newErrors.customer_phone = 'Please enter a valid Nigerian phone number';
      }
    }

    // Customer email is optional but validate format if provided
    if (customerEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(customerEmail.trim())) {
        newErrors.customer_email = 'Please enter a valid email address';
      }
    }

    if (pricingMode === 'per_person') {
      const players = parseInt(numberOfPlayers);
      if (!numberOfPlayers || isNaN(players) || players < minPlayers) {
        newErrors.players = `Number of players is required (min ${minPlayers})`;
      } else if (players > maxPlayers) {
        newErrors.players = `Maximum ${maxPlayers} players allowed`;
      }
    }

    if (!price || parseFloat(price) <= 0) {
      newErrors.price = 'Price must be greater than zero';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (activeTab === 'single') {
        // Single session booking
        const bookingData: ManualBookingData = {
          court_id: courtId,
          booking_date: date,
          start_time: startTime,
          end_time: endTime,
          customer_name: customerName.trim(),
          customer_phone: customerPhone.trim() || undefined,
          customer_email: customerEmail.trim() || undefined,
          price: parseFloat(price),
          payment_method: paymentMethod,
          booking_source: bookingSource,
          number_of_players: numberOfPlayers ? parseInt(numberOfPlayers) : undefined,
          notes: notes.trim() || undefined,
        };

        await onSubmit(bookingData);
      } else {
        // Recurring bookings
        const recurringData: RecurringManualBookingData = {
          sessions: sessions,
          customer_name: customerName.trim(),
          customer_phone: customerPhone.trim() || undefined,
          customer_email: customerEmail.trim() || undefined,
          payment_method: paymentMethod,
          booking_source: bookingSource,
          number_of_players: numberOfPlayers ? parseInt(numberOfPlayers) : undefined,
          notes: notes.trim() || undefined,
        };

        // Call recurring API directly
        await vendorApi.createRecurringManualBookings(venueId, recurringData);

        // Invalidate queries to refresh data immediately (for recurring bookings)
        // Single bookings are handled by parent's onSubmit which already invalidates
        queryClient.invalidateQueries({ queryKey: ['bookings'] });
        queryClient.invalidateQueries({ queryKey: ['schedule'] });
        queryClient.invalidateQueries({ queryKey: ['peakHours'] });
        queryClient.invalidateQueries({ queryKey: ['earnings'] });
        queryClient.invalidateQueries({ queryKey: ['manual-booking-customers'] });
      }

      onClose();
    } catch (error: any) {
      // Handle specific error messages
      if (error.response?.data?.message) {
        setErrors({ submit: error.response.data.message });
      } else if (error.message) {
        setErrors({ submit: error.message });
      } else {
        setErrors({ submit: 'Failed to create manual booking. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" animate={false}>
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl font-bold text-gray-900">Add Manual Booking</h2>
              <p className="text-xs sm:text-sm text-gray-500">Walk-in or phone booking</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50 px-4 sm:px-6">
          <div className="flex gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => {
                setActiveTab('single');
                setErrors({});
              }}
              className={`flex-1 sm:flex-none px-3 sm:px-6 py-2.5 sm:py-3 font-medium text-xs sm:text-sm transition-all ${
                activeTab === 'single'
                  ? 'border-b-2 border-green-600 text-green-600 bg-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <User className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              Single
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveTab('recurring');
                setErrors({});
              }}
              className={`flex-1 sm:flex-none px-3 sm:px-6 py-2.5 sm:py-3 font-medium text-xs sm:text-sm transition-all ${
                activeTab === 'recurring'
                  ? 'border-b-2 border-green-600 text-green-600 bg-white'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Repeat className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1 sm:mr-2" />
              Recurring
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Court Selection */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
              <MapPin className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
              Court/Field *
            </label>
            <select
              value={courtId}
              onChange={(e) => setCourtId(Number(e.target.value))}
              className={`w-full px-3 py-2.5 sm:py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                errors.court_id ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value={0}>Select a court</option>
              {Array.isArray(courts) && courts.map((court) => (
                <option key={court.id} value={court.id}>
                  {court.name} {court.sport && `- ${court.sport.name}`}
                </option>
              ))}
            </select>
            {errors.court_id && (
              <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                {errors.court_id}
              </p>
            )}
          </div>

          {/* Date Selection - Single or Recurring */}
          {activeTab === 'single' ? (
            <div className="overflow-hidden">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                Date *
              </label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className={`w-full min-w-0 max-w-full px-3 py-2.5 sm:py-2 text-sm border rounded-xl sm:rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent box-border ${
                  errors.booking_date ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.booking_date && (
                <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {errors.booking_date}
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-hidden">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                Recurring Sessions * ({sessions.length} sessions)
              </label>

              {/* Smart Fill */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-3">
                <p className="text-xs font-semibold text-green-800 mb-2">⚡ Quick Fill — generate dates automatically</p>

                {/* Start date */}
                <div className="mb-2">
                  <p className="text-xs text-gray-600 mb-1.5">Starting from:</p>
                  <input
                    type="date"
                    value={smartFillStartDate}
                    onChange={(e) => setSmartFillStartDate(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                </div>

                {/* Day toggles */}
                <div className="mb-2">
                  <p className="text-xs text-gray-600 mb-1.5">Repeat every:</p>
                  <div className="flex gap-1 flex-wrap">
                    {[
                      { label: 'Mon', day: 1 }, { label: 'Tue', day: 2 }, { label: 'Wed', day: 3 },
                      { label: 'Thu', day: 4 }, { label: 'Fri', day: 5 }, { label: 'Sat', day: 6 },
                      { label: 'Sun', day: 0 },
                    ].map(({ label, day }) => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => setSmartFillDays(
                          smartFillDays.includes(day)
                            ? smartFillDays.filter(d => d !== day)
                            : [...smartFillDays, day]
                        )}
                        className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors ${
                          smartFillDays.includes(day)
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Duration pills */}
                <div className="mb-2">
                  <p className="text-xs text-gray-600 mb-1.5">For:</p>
                  <div className="flex gap-1 flex-wrap">
                    {([
                      { value: '1m', label: '1 Month' },
                      { value: '3m', label: '3 Months' },
                      { value: '6m', label: '6 Months' },
                      { value: '12m', label: '1 Year' },
                      { value: 'custom', label: 'Custom' },
                    ] as const).map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setSmartFillDuration(value)}
                        className={`px-2.5 py-1 text-xs font-semibold rounded-lg border transition-colors ${
                          smartFillDuration === value
                            ? 'bg-green-600 text-white border-green-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:border-green-400'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom end date */}
                {smartFillDuration === 'custom' && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-600 mb-1.5">Until:</p>
                    <input
                      type="date"
                      value={smartFillEndDate}
                      onChange={(e) => setSmartFillEndDate(e.target.value)}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                )}

                {errors.smartFill && (
                  <p className="text-xs text-red-600 flex items-center gap-1 mb-2">
                    <AlertCircle className="w-3.5 h-3.5" />
                    {errors.smartFill}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleSmartFill}
                  className="w-full py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 transition-colors"
                >
                  Generate Sessions
                </button>
              </div>

              {/* Add Date Input — manual fallback */}
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <input
                  type="date"
                  value={customDate}
                  onChange={(e) => setCustomDate(e.target.value)}
                  className="w-full sm:flex-1 min-w-0 max-w-full px-3 py-2.5 sm:py-2 text-sm border border-gray-300 rounded-xl sm:rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent box-border"
                />
                <button
                  type="button"
                  onClick={handleAddDate}
                  disabled={!customDate || !startTime || !endTime || !courtId || !price}
                  className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-white border border-green-600 text-green-700 text-sm rounded-xl sm:rounded-lg font-medium hover:bg-green-50 disabled:border-gray-300 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                  title={!startTime || !endTime || !courtId || !price ? "Please fill in time, court, and price fields first" : ""}
                >
                  + Add One Date
                </button>
              </div>

              <p className="text-xs text-gray-500 mb-3">
                💡 Fill in court, time, and price first — then use Quick Fill or add dates manually.
              </p>

              {/* Sessions List */}
              {sessions.length > 0 && (
                <div className="border border-gray-300 rounded-lg p-3 max-h-60 overflow-y-auto mb-3">
                  <div className="space-y-2">
                    {sessions.map((session, index) => (
                      <div
                        key={index}
                        className="bg-green-50 p-3 rounded-lg border border-green-200"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-semibold text-gray-900">
                            Session {index + 1}: {new Date(session.date + 'T00:00').toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric',
                            })}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveSession(index)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="text-xs text-gray-600 space-y-1">
                          <div>⏰ {session.start_time} - {session.end_time}</div>
                          <div>🏟️ {courts.find(c => c.id === session.court_id)?.name || 'Court ' + session.court_id}</div>
                          <div>💰 {CURRENCY_SYMBOL}{session.price.toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {errors.sessions && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.sessions}
                </p>
              )}
            </div>
          )}

          {/* Time Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                Start Time *
              </label>
              <select
                value={startTime}
                onChange={(e) => {
                  const newStart = e.target.value;
                  setStartTime(newStart);
                  // Clear end time if it's no longer valid (use index-based comparison for midnight)
                  const newStartIdx = timeOptions.indexOf(newStart);
                  const currentEndIdx = timeOptions.indexOf(endTime);
                  if (endTime && newStartIdx >= 0 && currentEndIdx >= 0 && newStartIdx >= currentEndIdx) {
                    setEndTime('');
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.start_time ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">Select time</option>
                {timeOptions.map((time) => (
                  <option key={time} value={time}>
                    {time}
                  </option>
                ))}
              </select>
              {errors.start_time && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.start_time}
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                {pricingSlabs.length > 0 ? 'Duration *' : 'End Time *'}
              </label>
              {pricingSlabs.length > 0 ? (
                <select
                  value={endTime}
                  onChange={(e) => {
                    const selectedEnd = e.target.value;
                    setEndTime(selectedEnd);
                    // Auto-fill price from the matching slab
                    if (startTime && selectedEnd) {
                      const [sh, sm] = startTime.split(':').map(Number);
                      const [eh, em] = selectedEnd.split(':').map(Number);
                      const durationMins = (eh * 60 + em) - (sh * 60 + sm);
                      const durationH = durationMins / 60;
                      const slab = pricingSlabs.find(s => Math.abs(s.duration_hours - durationH) < 0.01);
                      if (slab) setPrice(slab.price.toString());
                    }
                  }}
                  className={`w-full px-3 py-2.5 sm:py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.end_time ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{startTime ? 'Select duration' : 'Select start time first'}</option>
                  {pricingSlabs
                    .slice()
                    .sort((a, b) => a.duration_hours - b.duration_hours)
                    .map((slab) => {
                      if (!startTime) return null;
                      const [sh, sm] = startTime.split(':').map(Number);
                      const totalMins = sh * 60 + sm + Math.round(slab.duration_hours * 60);
                      const eh = Math.floor(totalMins / 60) % 24;
                      const em = totalMins % 60;
                      const endStr = `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
                      const durLabel = (() => {
                        const totalM = Math.round(slab.duration_hours * 60);
                        const h = Math.floor(totalM / 60);
                        const m = totalM % 60;
                        return m > 0 ? `${h}hr ${m}min` : `${h}hr`;
                      })();
                      return (
                        <option key={slab.duration_hours} value={endStr}>
                          {durLabel} (until {endStr}) — {CURRENCY_SYMBOL}{slab.price.toLocaleString()}
                        </option>
                      );
                    })}
                </select>
              ) : (
                <select
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className={`w-full px-3 py-2.5 sm:py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.end_time ? 'border-red-500' : 'border-gray-300'
                  }`}
                >
                  <option value="">{startTime ? `After ${startTime}` : 'Select time'}</option>
                  {endTimeOptions.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              )}
              {errors.end_time && (
                <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {errors.end_time}
                </p>
              )}
            </div>
          </div>

          {/* Pricing Mode Toggle */}
          {courtPricingMode === 'both' && (
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                Pricing Mode
              </label>
              <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setPricingMode('hourly')}
                  className={`flex-1 py-2 text-xs sm:text-sm font-medium transition-colors ${
                    pricingMode === 'hourly'
                      ? 'bg-green-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Hourly ({CURRENCY_SYMBOL}{hourlyRate.toLocaleString()}/hr)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPricingMode('per_person');
                    if (!numberOfPlayers) setNumberOfPlayers(minPlayers.toString());
                  }}
                  className={`flex-1 py-2 text-xs sm:text-sm font-medium transition-colors ${
                    pricingMode === 'per_person'
                      ? 'bg-green-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Per Person ({CURRENCY_SYMBOL}{perPersonRate.toLocaleString()}/person)
                </button>
              </div>
            </div>
          )}

          {/* Per-person info banner when court only supports per_person */}
          {courtPricingMode === 'per_person' && perPersonRate > 0 && (
            <div className="text-xs bg-green-50 border border-green-200 rounded-lg p-2.5 text-green-700">
              This court uses per-person pricing at {CURRENCY_SYMBOL}{perPersonRate.toLocaleString()}/person
            </div>
          )}

          {/* Shared booking info banner */}
          {courtId > 0 && (() => {
            const selectedCourt = courts.find(c => c.id === courtId);
            return selectedCourt?.allow_shared_booking && (courtPricingMode === 'per_person' || courtPricingMode === 'both');
          })() && (
            <div className="text-xs bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-blue-700">
              <span className="font-medium">Shared court (drop-in):</span> Multiple customers can book this court at the same time, up to {maxPlayers} players. Each booking is independent.
            </div>
          )}

          {/* Pricing & Players */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <DollarSign className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                Price ({CURRENCY_SYMBOL}) *
              </label>
              <input
                type="text"
                inputMode="numeric"
                value={price ? Number(price).toLocaleString() : ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  setPrice(raw);
                }}
                placeholder="0"
                aria-label="Booking price in Naira"
                className={`w-full px-3 py-2.5 sm:py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.price ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.price && (
                <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {errors.price}
                </p>
              )}
              {/* Price breakdown hint */}
              {pricingMode === 'per_person' && perPersonRate > 0 && (
                <p className="mt-1 text-[11px] text-gray-400">
                  {CURRENCY_SYMBOL}{perPersonRate.toLocaleString()}/person × {parseInt(numberOfPlayers) || minPlayers} player{(parseInt(numberOfPlayers) || minPlayers) !== 1 ? 's' : ''} = {CURRENCY_SYMBOL}{(perPersonRate * (parseInt(numberOfPlayers) || minPlayers)).toLocaleString()}
                </p>
              )}
              {pricingMode === 'hourly' && hourlyRate > 0 && durationHours > 0 && (
                <p className="mt-1 text-[11px] text-gray-400">
                  {CURRENCY_SYMBOL}{hourlyRate.toLocaleString()}/hr × {durationHours % 1 === 0 ? durationHours : durationHours.toFixed(1)}hr{durationHours !== 1 ? 's' : ''} = {CURRENCY_SYMBOL}{(hourlyRate * durationHours).toLocaleString()}
                </p>
              )}
              {pricingMode === 'hourly' && hourlyRate > 0 && durationHours === 0 && startTime && endTime && (
                <p className="mt-1 text-[11px] text-gray-400">
                  {CURRENCY_SYMBOL}{hourlyRate.toLocaleString()}/hr
                </p>
              )}
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 inline mr-1" />
                Players {pricingMode === 'per_person' ? '*' : <span className="text-gray-400 text-xs">(Optional)</span>}
              </label>
              <input
                type="number"
                value={numberOfPlayers}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val && pricingMode === 'per_person') {
                    const num = parseInt(val);
                    if (num < minPlayers) return setNumberOfPlayers(minPlayers.toString());
                    if (num > maxPlayers) return setNumberOfPlayers(maxPlayers.toString());
                  }
                  setNumberOfPlayers(val);
                }}
                placeholder={pricingMode === 'per_person' ? `${minPlayers} - ${maxPlayers}` : 'e.g., 4'}
                min={pricingMode === 'per_person' ? minPlayers : 1}
                max={pricingMode === 'per_person' ? maxPlayers : undefined}
                aria-label="Number of players"
                className={`w-full px-3 py-2.5 sm:py-2 text-sm border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  pricingMode === 'per_person' && !numberOfPlayers ? 'border-amber-300' : 'border-gray-300'
                }`}
              />
              {pricingMode === 'per_person' && errors.players && (
                <p className="mt-1 text-xs sm:text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  {errors.players}
                </p>
              )}
              {pricingMode === 'per_person' && !errors.players && (
                <p className="mt-1 text-[11px] text-gray-400">
                  Min: {minPlayers}, Max: {maxPlayers}
                </p>
              )}
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-blue-50 rounded-xl p-3 sm:p-4 border border-blue-200 space-y-3 sm:space-y-4">
            <h3 className="font-semibold text-sm sm:text-base text-gray-900 flex items-center gap-2">
              <User className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
              Customer Information
            </h3>

            <div className="relative" ref={customerInputRef}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <User className="w-4 h-4 inline mr-1" />
                Customer Name *
              </label>

              {/* Show customer count hint */}
              {customers.length > 0 && (
                <div className="mb-2 text-xs bg-blue-50 border border-blue-200 rounded p-2 text-blue-700">
                  💡 {customers.length} customer{customers.length > 1 ? 's' : ''} found (Spotts users & walk-ins). Click the field to see them or start typing to search.
                </div>
              )}

              <div className="relative">
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCustomerName(value);
                    setSearchQuery(value);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => {
                    setSearchQuery(customerName);
                    setShowCustomerDropdown(true);
                  }}
                  placeholder={customers.length > 0 ? "Search existing or type new name..." : "Enter customer name"}
                  className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.customer_name ? 'border-red-500' : 'border-gray-300'
                  }`}
                />

                {/* Search icon */}
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔍
                </div>
              </div>

              {/* Customer Search Dropdown */}
              {showCustomerDropdown && filteredCustomers.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border-2 border-green-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                  <div className="sticky top-0 p-3 text-xs font-semibold text-green-800 bg-green-50 border-b-2 border-green-200">
                    {searchQuery
                      ? `Found ${filteredCustomers.length} matching customer(s)${filteredCustomers.length === 50 ? ' (showing first 50)' : ''}`
                      : `${filteredCustomers.length} Recent Customer(s)${filteredCustomers.length === 50 ? ' (showing first 50)' : ''}`
                    }
                  </div>
                  {filteredCustomers.map((customer, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => handleSelectCustomer(customer)}
                      className="w-full px-4 py-3 text-left hover:bg-green-50 focus:bg-green-100 focus:outline-none border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">{customer.name}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                              customer.source === 'spotts'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {customer.source === 'spotts' ? 'Spotts' : 'Walk-in'}
                            </span>
                          </div>
                          {customer.phone && (
                            <div className="text-sm text-gray-600 mt-1">📱 {customer.phone}</div>
                          )}
                          {customer.email && (
                            <div className="text-sm text-gray-600 mt-0.5">✉️ {customer.email}</div>
                          )}
                        </div>
                        <div className="text-green-600 text-sm font-medium">Select →</div>
                      </div>
                    </button>
                  ))}
                  <div className="sticky bottom-0 p-2 text-xs text-center text-gray-500 bg-gray-50 border-t">
                    Or continue typing to add new customer
                  </div>
                </div>
              )}

              {errors.customer_name && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.customer_name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Phone className="w-4 h-4 inline mr-1" />
                Customer Phone <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="tel"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                placeholder="+234... (optional)"
                aria-label="Customer phone number"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.customer_phone ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.customer_phone && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.customer_phone}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                Customer Email <span className="text-gray-400">(Optional)</span>
              </label>
              <input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="customer@email.com (optional)"
                aria-label="Customer email address"
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                  errors.customer_email ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.customer_email ? (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {errors.customer_email}
                </p>
              ) : (
                <p className="mt-1 text-xs text-gray-500">
                  If provided, a booking confirmation email will be sent to the customer.
                </p>
              )}
            </div>
          </div>

          {/* Payment Method Selection */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              Payment Method *
            </label>
            <div className="flex gap-1.5 sm:gap-2">
              {PAYMENT_METHODS.map((method) => (
                <button
                  key={method.value}
                  type="button"
                  onClick={() => setPaymentMethod(method.value as ManualBookingData['payment_method'])}
                  className={`flex-1 py-2.5 sm:py-2.5 rounded-xl sm:rounded-lg font-semibold text-xs sm:text-sm transition-all border ${
                    paymentMethod === method.value
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-green-400'
                  }`}
                >
                  {method.icon} {method.label}
                </button>
              ))}
            </div>
          </div>

          {/* Booking Source */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
              Booking Source *
            </label>
            <div className="flex gap-1.5 sm:gap-2">
              {(['walk_in', 'phone', 'whatsapp'] as const).map((source) => (
                <button
                  key={source}
                  type="button"
                  onClick={() => setBookingSource(source)}
                  className={`flex-1 py-2.5 sm:py-2.5 rounded-xl sm:rounded-lg font-semibold text-xs sm:text-sm transition-all border ${
                    bookingSource === source
                      ? 'bg-green-600 text-white border-green-600'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-green-400'
                  }`}
                >
                  {source === 'walk_in' ? '🚶 Walk-in' : source === 'whatsapp' ? '💬 WhatsApp' : '📞 Phone'}
                </button>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 inline mr-1" />
              Notes <span className="text-gray-500 font-normal">(Optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any additional notes about this booking..."
              rows={3}
              maxLength={1000}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">{notes.length}/1000 characters</p>
          </div>

          {/* Info Banner */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 sm:p-4">
            <p className="text-xs sm:text-sm text-yellow-800 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Manual Booking:</strong> 100% revenue to you, no platform commission.
              </span>
            </p>
          </div>

          {/* Error Message */}
          {errors.submit && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 flex items-center gap-2">
                <AlertCircle className="w-5 h-5" />
                {errors.submit}
              </p>
            </div>
          )}

          {/* Footer Actions */}
          <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="w-full sm:w-auto sm:flex-1 px-4 py-3 sm:py-2.5 bg-gray-200 text-gray-700 rounded-xl sm:rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto sm:flex-1 px-4 py-3 sm:py-2.5 bg-green-600 text-white rounded-xl sm:rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  {activeTab === 'single' ? (
                    <>
                      <User className="w-4 h-4" />
                      Add Booking
                    </>
                  ) : (
                    <>
                      <Repeat className="w-4 h-4" />
                      Create {sessions.length} Booking{sessions.length !== 1 ? 's' : ''}
                    </>
                  )}
                </>
              )}
            </button>
          </div>
        </form>
        </div>
    </Modal>
  );
}
